import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem, 
  Fab,
  Tooltip 
} from '@mui/material';
import { 
  Add as AddIcon, 
  TextFields, 
  Image, 
  ViewModule,
  Title,
  SmartButton,
  HorizontalRule,
  Height,
  Code
} from '@mui/icons-material';
import useEmailEditorStore from '../../utilities/emailEditorStore';
import { BLOCK_TYPES } from '../../utilities/blockTypes';
import EmailEditorBlock from './EmailEditorBlock';


function ContainerBlock({ id, type, properties, children = [] }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  const { addBlock, selectBlock, selectedBlockId } = useEmailEditorStore();
  const isSelected = selectedBlockId === id;

  const handleAddClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAddChild = (childType) => {
    addBlock(childType, id);
    handleClose();
  };

  const handleContainerClick = (event) => {
    event.stopPropagation();
    selectBlock(id);
  };

  const blockTypes = [
    { 
      type: BLOCK_TYPES.HEADER, 
      label: 'Header Block', 
      icon: <Title /> 
    },
    { 
      type: BLOCK_TYPES.TEXT, 
      label: 'Text Block', 
      icon: <TextFields /> 
    },
    { 
      type: BLOCK_TYPES.IMAGE, 
      label: 'Image Block', 
      icon: <Image /> 
    },
    { 
      type: BLOCK_TYPES.BUTTON, 
      label: 'Button Block', 
      icon: <SmartButton /> 
    },
    { 
      type: BLOCK_TYPES.CONTAINER, 
      label: 'Container Block', 
      icon: <ViewModule /> 
    },
     { 
      type: BLOCK_TYPES.COLUMNCONTAINER, 
      label: 'Column Block', 
      icon: <ViewModule /> 
    },
    { 
      type: BLOCK_TYPES.COLUMN, 
      label: 'Column Block', 
      icon: <ViewModule /> 
    },
    { 
      type: BLOCK_TYPES.DIVIDER, 
      label: 'Divider Block', 
      icon: <HorizontalRule /> 
    },
    { 
      type: BLOCK_TYPES.SPACER, 
      label: 'Spacer Block', 
      icon: <Height /> 
    },
    { 
      type: BLOCK_TYPES.HTMLCONTENT, 
      label: 'HTML Content Block', 
      icon: <Code /> 
    }
  ];

  const isEmpty = !children || children.length === 0;

  // Convert padding object to CSS string
  const getPaddingStyle = (padding) => {
    if (typeof padding === 'object') {
      return `${padding.top || 0}px ${padding.right || 0}px ${padding.bottom || 0}px ${padding.left || 0}px`;
    }
    return padding || '20px';
  };

  // Convert margin object to CSS string
  const getMarginStyle = (margin) => {
    if (typeof margin === 'object') {
      return `${margin.top || 0}px ${margin.right || 0}px ${margin.bottom || 0}px ${margin.left || 0}px`;
    }
    return margin || '10px 0';
  };

  // Convert border object to CSS string
  const getBorderStyle = (border) => {
    if (typeof border === 'object') {
      return `${border.width || 0}px ${border.style || 'solid'} ${border.color || '#000000'}`;
    }
    return border || 'none';
  };

  return (
    <Box
      onClick={handleContainerClick}
      sx={{
        backgroundColor: properties.backgroundColor || 'transparent',
        padding: getPaddingStyle(properties.padding),
        margin: getMarginStyle(properties.margin),
        borderRadius: `${properties.borderRadius || 0}px`,
        border: getBorderStyle(properties.border),
        minHeight: `${properties.minHeight || 100}px`,
        width: properties.width || '100%',
        maxWidth: properties.maxWidth || '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: properties.direction || 'column',
        justifyContent: properties.justifyContent || 'flex-start',
        alignItems: properties.alignItems || 'stretch',
        gap: `${properties.gap || 10}px`,
        cursor: 'pointer',
        outline: isSelected ? '2px solid #1976d2' : 'none',
        outlineOffset: '2px',
        transition: 'outline 0.2s ease-in-out',
        '&:hover': {
          outline: isSelected ? '2px solid #1976d2' : '1px dashed #ccc',
        },
        '&:hover .add-button': {
          opacity: 1
        }
      }}
    >
      {isEmpty ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: `${properties.minHeight || 100}px`,
            flexGrow: 1,
            border: '2px dashed #e0e0e0',
            borderRadius: '4px',
            color: '#666'
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Container Block - Click the + button to add child blocks
          </Typography>
        </Box>
      ) : (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: properties.direction || 'column',
            gap: `${properties.gap || 10}px`,
            width: '100%',
            height: '100%'
          }}
        >
          {children.map((child,index) => (
            
            <EmailEditorBlock
                    key={index}
                    blockType={child.type}
                    blockProperties={child.properties}
                    blockId={child.id}
                    blockParentId= {child.parentId}
                    blockChildren={child.children}
                    />

          ))}
        </Box>
      )}

      {/* Add Button */}
      <Tooltip title="Add child block">
        <Fab
          size="small"
          color="primary"
          className="add-button"
          onClick={(e) => {
            e.stopPropagation();
            handleAddClick(e);
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            opacity: isSelected ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
            width: 32,
            height: 32,
            minHeight: 32,
            zIndex: 10,
            '&:hover': {
              opacity: 1
            }
          }}
        >
          <AddIcon fontSize="small" />
        </Fab>
      </Tooltip>

      {/* Block Type Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            maxHeight: 400
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {blockTypes.map((blockType) => (
          <MenuItem
            key={blockType.type}
            onClick={() => handleAddChild(blockType.type)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1
            }}
          >
            {blockType.icon}
            <Typography variant="body2">{blockType.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}



export default ContainerBlock;